import type {
  AccessRoleType,
  BusinessScale,
  BusinessType,
  DietaryType,
  InvitationStatus,
  KotStatus,
  OrderSource,
  OrderStatus,
  OrderType,
  PaymentMethod,
  StaffSubRoleType,
  TableStatus,
  TableType,
} from '@billx/types';

const TABLE_TYPE_LABELS: Record<TableType, string> = Object.freeze({
  casual: 'Casual',
  outdoor: 'Outdoor',
  private: 'Private',
  booth: 'Booth',
  dining: 'Dining',
  bar: 'Bar',
});

const TABLE_STATUS_LABELS: Record<TableStatus, string> = Object.freeze({
  available: 'Available',
  occupied: 'Occupied',
  reserved: 'Reserved',
  cleaning: 'Cleaning',
  maintenance: 'Maintenance',
});

const ORDER_TYPE_LABELS: Record<OrderType, string> = Object.freeze({
  dine_in: 'Dine-In',
  take_away: 'Take Away',
  delivery: 'Delivery',
});

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = Object.freeze({
  requested: 'Awaiting Approval',
  active: 'Active',
  paid: 'Paid',
  cancelled: 'Cancelled',
});

const ORDER_SOURCE_LABELS: Record<OrderSource, string> = Object.freeze({
  pos: 'POS',
  qr: 'QR',
  online: 'Online',
});

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = Object.freeze({
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
});

const KOT_STATUS_LABELS: Record<KotStatus, string> = Object.freeze({
  pending: 'Pending',
  preparing: 'Preparing',
  ready: 'Ready',
  done: 'Done',
  requested: 'Awaiting Approval',
  rejected: 'Rejected',
});

const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> =
  Object.freeze({
    pending: 'Pending',
    accepted: 'Accepted',
    declined: 'Declined',
  });

const ACCESS_ROLE_LABELS: Record<AccessRoleType, string> = Object.freeze({
  owner: 'Owner',
  manager: 'Manager',
  analyst: 'Analyst',
  staff: 'Staff',
  unassigned: 'Unassigned',
});

// Staff sub-role labels
const STAFF_SUBROLE_LABELS: Record<StaffSubRoleType, string> = Object.freeze({
  supervisor: 'Supervisor',
  kitchen: 'Kitchen Staff',
  delivery: 'Delivery Staff',
  waiter: 'Waiter',
  cashier: 'Cashier',
});

const ACCESS_SUBROLE_LABELS: Record<StaffSubRoleType, string> = Object.freeze({
  ...STAFF_SUBROLE_LABELS,
});

const BUSINESS_SCALE_LABELS: Record<BusinessScale, string> = Object.freeze({
  single_outlet: 'Single Outlet',
  multi_outlet: 'Multi Outlet',
  enterprise: 'Enterprise',
});

const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = Object.freeze({
  retail_store: 'Retail Store',
  restaurant: 'Restaurant',
  fine_dining: 'Fine Dining',
  cafe: 'Cafe',
  bakery: 'Bakery',
  pharmacy: 'Pharmacy',
  food_truck: 'Food Truck',
  cloud_kitchen: 'Cloud Kitchen',
  bar: 'Bar',
  other: 'Other',
});

const DIETARY_TYPE_LABELS: Record<DietaryType, string> = Object.freeze({
  veg: 'Veg',
  'non-veg': 'Non-Veg',
  egg: 'Egg',
});

export const TABLE_TYPES = Object.keys(TABLE_TYPE_LABELS) as TableType[];
export const TABLE_STATUSES = Object.keys(TABLE_STATUS_LABELS) as TableStatus[];
export const ORDER_TYPES = Object.keys(ORDER_TYPE_LABELS) as OrderType[];
export const ORDER_STATUSES = Object.keys(ORDER_STATUS_LABELS) as OrderStatus[];
export const ORDER_SOURCE_OPTIONS = Object.keys(
  ORDER_SOURCE_LABELS
) as OrderSource[];
export const PAYMENT_METHODS = Object.keys(
  PAYMENT_METHOD_LABELS
) as PaymentMethod[];
export const KOT_STATUSES = Object.keys(KOT_STATUS_LABELS) as KotStatus[];
export const INVITATION_STATUSES = Object.keys(
  INVITATION_STATUS_LABELS
) as InvitationStatus[];
export const ACCESS_ROLES = Object.keys(ACCESS_ROLE_LABELS) as AccessRoleType[];
export const STAFF_SUBROLES = Object.keys(
  STAFF_SUBROLE_LABELS
) as StaffSubRoleType[];

export const ACCESS_SUBROLES = Object.keys(
  ACCESS_SUBROLE_LABELS
) as StaffSubRoleType[];
export const BUSINESS_SCALES = Object.keys(
  BUSINESS_SCALE_LABELS
) as BusinessScale[];
export const BUSINESS_TYPES = Object.keys(
  BUSINESS_TYPE_LABELS
) as BusinessType[];
export const DIETARY_TYPES = Object.keys(DIETARY_TYPE_LABELS) as DietaryType[];

// Type-safe label getter utility
type LabelType =
  | 'OrderType'
  | 'OrderStatus'
  | 'OrderSource'
  | 'PaymentMethod'
  | 'KotStatus'
  | 'InvitationStatus'
  | 'AccessRole'
  | 'StaffSubRole'
  | 'TableType'
  | 'TableStatus'
  | 'BusinessScale'
  | 'BusinessType'
  | 'DietaryType';

type LabelValue<T extends LabelType> = T extends 'OrderType'
  ? OrderType
  : T extends 'OrderStatus'
    ? OrderStatus
    : T extends 'OrderSource'
      ? OrderSource
      : T extends 'PaymentMethod'
        ? PaymentMethod
        : T extends 'KotStatus'
          ? KotStatus
          : T extends 'InvitationStatus'
            ? InvitationStatus
            : T extends 'AccessRole'
              ? AccessRoleType
              : T extends 'TableType'
                ? TableType
                : T extends 'TableStatus'
                  ? TableStatus
                  : T extends 'BusinessScale'
                    ? BusinessScale
                    : T extends 'BusinessType'
                      ? BusinessType
                      : T extends 'DietaryType'
                        ? DietaryType
                        : never;

/**
 * Type-safe utility to get label for a given type and value
 * @param type - The label type (e.g., 'OrderType', 'OrderStatus')
 * @param value - The value to get the label for
 * @returns The corresponding label string
 *
 */
export function getLabel<T extends LabelType>(
  type: T,
  value: LabelValue<T>
): string {
  switch (type) {
    case 'OrderType':
      return ORDER_TYPE_LABELS[value as OrderType];
    case 'OrderStatus':
      return ORDER_STATUS_LABELS[value as OrderStatus];
    case 'OrderSource':
      return ORDER_SOURCE_LABELS[value as OrderSource];
    case 'PaymentMethod':
      return PAYMENT_METHOD_LABELS[value as PaymentMethod];
    case 'KotStatus':
      return KOT_STATUS_LABELS[value as KotStatus];
    case 'InvitationStatus':
      return INVITATION_STATUS_LABELS[value as InvitationStatus];
    case 'AccessRole':
      return ACCESS_ROLE_LABELS[value as AccessRoleType];
    case 'StaffSubRole':
      return STAFF_SUBROLE_LABELS[value as StaffSubRoleType];
    case 'TableType':
      return TABLE_TYPE_LABELS[value as TableType];
    case 'TableStatus':
      return TABLE_STATUS_LABELS[value as TableStatus];
    case 'BusinessScale':
      return BUSINESS_SCALE_LABELS[value as BusinessScale];
    case 'BusinessType':
      return BUSINESS_TYPE_LABELS[value as BusinessType];
    case 'DietaryType':
      return DIETARY_TYPE_LABELS[value as DietaryType];
    default:
      return '';
  }
}
